import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Spinner, EmptyState, Badge, severityBadgeMap } from '../components/UI';
import { fmtDateTime } from '../utils/format';

function Section({ icon, title, tooltip, children }) {
  return (
    <div className="sms-card h-100 sms-widget-fade">
      <div className="d-flex align-items-center gap-2 mb-3">
        <i className={`bi ${icon}`} style={{ color: 'var(--sms-acc)', fontSize: 14 }} aria-hidden="true" />
        <h6 className="mb-0 fw-semibold d-flex align-items-center gap-1" style={{ color: 'var(--sms-txt)', fontSize: 13 }}>
          {title}
          {tooltip && (
            <i className="bi bi-info-circle" style={{ fontSize: 12, color: 'var(--sms-txt2)', cursor: 'help' }} title={tooltip} />
          )}
        </h6>
      </div>
      {children}
    </div>
  );
}

export default function InsightsPage() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    api.get('/dashboard/insights')
      .then(r => setData(r.data.data))
      .catch(e => setError(e.response?.data?.message || 'Could not load insights'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;
  if (error)   return <EmptyState icon="bi-exclamation-triangle" message={error} />;
  if (!data)   return <EmptyState message="Could not load insights" />;

  const { ai, generated_at } = data;

  return (
    <>
      {/* Header */}
      <div className="mb-4">
        <h5 className="mb-1 fw-bold" style={{ color: 'var(--sms-txt)', fontSize: 20 }}>Insights</h5>
        <p style={{ fontSize: 13, color: 'var(--sms-txt2)', marginBottom: 0 }}>
          Smart summary of today's key insights and recommended actions
          <span style={{ margin: '0 8px', color: 'var(--sms-txt2)' }}>|</span>
          Generated {fmtDateTime(generated_at)}
        </p>
      </div>

      {/* Summary */}
      <div className="row g-3 mb-3">
        <div className="col-12">
          <Section icon="bi-stars" title="Today's summary"
            tooltip="An AI-generated overview of today's dashboard data — student workload, biggest risks, and what to prioritize.">
            <p style={{ fontSize: 13, color: 'var(--sms-txt)', lineHeight: 1.6, marginBottom: 0 }}>{ai.summary}</p>
          </Section>
        </div>
      </div>

      {/* Students to watch + Priority actions */}
      <div className="row g-3 mb-3">
        <div className="col-12 col-xl-6">
          <Section icon="bi-person-exclamation" title="Students to watch"
            tooltip="Students flagged for below-75% attendance (last 30 days) or a below-50% academic average, with the highest-risk students listed first.">
            {!ai.students_to_watch?.length ? <EmptyState icon="bi-check-circle" message="No students flagged" /> : (
              <div className="d-flex flex-column">
                {ai.students_to_watch.map((s, i) => (
                  <div key={i} className="d-flex align-items-start gap-2 py-2"
                    style={{ borderBottom: i < ai.students_to_watch.length - 1 ? '1px solid var(--sms-bdr)' : 'none' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--sms-txt)' }}>
                        {s.name} {s.intake && <span style={{ fontWeight: 400, color: 'var(--sms-txt2)', fontSize: 12 }}>· {s.intake}</span>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--sms-txt2)', marginTop: 2 }}>{s.why}</div>
                    </div>
                    <Badge value={String(s.risk || '').toLowerCase()} map={severityBadgeMap} />
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>

        <div className="col-12 col-xl-6">
          <Section icon="bi-list-check" title="Priority actions"
            tooltip="Actionable items for today, drawn from aging pending requests, open disciplinary cases, and intakes nearing capacity.">
            {!ai.priority_actions?.length ? <EmptyState icon="bi-check-circle" message="Nothing urgent right now" /> : (
              <div className="d-flex flex-column">
                {ai.priority_actions.map((a, i) => (
                  <div key={i} className="py-2"
                    style={{ borderBottom: i < ai.priority_actions.length - 1 ? '1px solid var(--sms-bdr)' : 'none' }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--sms-txt)' }}>{a.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--sms-txt2)', marginTop: 2 }}>{a.why}</div>
                    <div style={{ fontSize: 12, color: 'var(--sms-acc)', marginTop: 2 }}>{a.suggested_action}</div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      </div>

      {/* Insight blurbs */}
      <div className="row g-3">
        <div className="col-12 col-md-6 col-xl-3">
          <Section icon="bi-calendar-check" title="Attendance"
            tooltip="How many students are below the 75% attendance threshold over the last 30 days, and who to prioritize first.">
            <p style={{ fontSize: 12, color: 'var(--sms-txt2)', lineHeight: 1.5, marginBottom: 0 }}>{ai.attendance_insight}</p>
          </Section>
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <Section icon="bi-graph-up" title="Academic"
            tooltip="How many students are averaging below 50% across their assessments, and who may need academic support.">
            <p style={{ fontSize: 12, color: 'var(--sms-txt2)', lineHeight: 1.5, marginBottom: 0 }}>{ai.academic_insight}</p>
          </Section>
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <Section icon="bi-envelope-open" title="Requests"
            tooltip="The pending student-request backlog and how long the oldest request has been waiting.">
            <p style={{ fontSize: 12, color: 'var(--sms-txt2)', lineHeight: 1.5, marginBottom: 0 }}>{ai.requests_insight}</p>
          </Section>
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <Section icon="bi-people" title="Capacity"
            tooltip="Active intakes at or above 85% of their enrollment capacity.">
            <p style={{ fontSize: 12, color: 'var(--sms-txt2)', lineHeight: 1.5, marginBottom: 0 }}>{ai.capacity_insight}</p>
          </Section>
        </div>
      </div>
    </>
  );
}
